import React, { createContext, useState, useEffect, useContext, useMemo } from 'react';
import { apiBaseUrl } from '../config';
import axios from 'axios';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('userToken'));

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchUser = async (tokenToUse = token) => {
    try {
      const response = await axios.get(`${apiBaseUrl}/user/me`, {
        headers: {
          Authorization: `Bearer ${tokenToUse}`
        },
        withCredentials: true
      });

      if (response.data.success) {
        setUser(response.data.data);
      }
    } catch (error) {
      localStorage.removeItem('userToken');
      setToken(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${apiBaseUrl}/user/me`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      if (response.data.success) {
        setUser(response.data.data);
      }
    } catch {
      /* ignore */
    }
  };

  const login = async (authToken) => {
    localStorage.setItem('userToken', authToken);
    setToken(authToken);
    await fetchUser(authToken);
  };

  const logout = () => {
    localStorage.removeItem('userToken');
    setToken(null);
    setUser(null);
  };

  const updateProfile = async (fields) => {
    const response = await axios.put(`${apiBaseUrl}/user/me`, fields, {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: true,
    });
    if (response.data.success) {
      setUser(response.data.data);
    }
    return response.data;
  };

  // Derived paper-trading access, from the user's subscribed_tools.
  //
  // Restored from 4280c79. Commit 85e7862 deleted this while rewriting the file,
  // but PaperTrading.js still destructures `paperTradingAccess` from useAuth().
  // With it gone the value was `undefined`, so `paperTradingAccess?.active` was
  // always falsy and the paywall showed even to subscribers — while the backend
  // correctly reported "you already have an active subscription", which is why
  // the two disagreed on screen.
  const paperTradingAccess = useMemo(() => {
    const tools = user?.subscribed_tools || [];
    // Most recent papertrading entry wins, so a renewal supersedes a lapsed one.
    const entry = [...tools]
      .filter(t => t.tool_id === 'papertrading')
      .sort((a, b) => new Date(b.subscribed_at) - new Date(a.subscribed_at))[0];
    if (!entry) return { active: false };
    const daysLeft = Math.ceil((new Date(entry.expires_at) - Date.now()) / 86400000);
    return {
      active:        daysLeft > 0,
      expires_at:    entry.expires_at,
      subscribed_at: entry.subscribed_at,
      days_left:     Math.max(0, daysLeft),
    };
  }, [user]);

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    refreshUser,
    updateProfile,
    paperTradingAccess,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
